import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Plus, DollarSign } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { salaryColumns, SalaryEmployee } from "../lib/salaryTableColumns";
import { SimpleDataTable } from "../components/simpleDataTable";

export default function Salary() {
  const navigate = useNavigate();
  const location = useLocation();
  const [employees, setEmployees] = useState<SalaryEmployee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      window.history.replaceState(null, "");

      setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    fetchEmployeesWithSalary();
  }, []);

  const fetchEmployeesWithSalary = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");

      // Use the backend endpoint that returns all employees with salary status
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/salary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const employeesData = response.data;

      // Map backend data to frontend SalaryEmployee type
      const mappedEmployees: SalaryEmployee[] = employeesData.map(
        (emp: any) => ({
          id: emp._id,
          name: emp.fullName,
          email: emp.email,
          phone: emp.phone,
          company: emp.company,
          designation: emp.designation,
          department: emp.department,
          employeeType: emp.employeeType || "Employee",
          salaryStatus: emp.salaryStatus,
          isActive: emp.isActive !== undefined ? emp.isActive : true,
          resignationDate: emp.resignationDate,
          salary: emp.salary,
        })
      );

      setEmployees(mappedEmployees);
    } catch (error: any) {
      console.error("Error fetching employees with salary:", error);
      setError(
        error.response?.data?.message || "Failed to fetch employees with salary"
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Salary</h1>
            <p className="text-muted-foreground">
              Manage employee salary information
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center h-[400px]">
          <div className="text-center space-y-4">
            <Spinner variant="ellipsis" size={40} className="text-primary" />
            <div>
              <h3 className="text-lg font-medium">Loading salaries...</h3>
              <p className="text-sm text-muted-foreground">
                Please wait while we fetch the data
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Salary</h1>
          <p className="text-muted-foreground">
            Manage employee salary information
          </p>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-green-500" />
            <p className="text-sm font-medium text-green-800">Success</p>
          </div>
          <p className="mt-1 text-sm text-green-700">{successMessage}</p>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 rounded-full bg-red-500" />
            <p className="text-sm font-medium text-red-800">Error</p>
          </div>
          <p className="mt-1 text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Total Employees
              </p>
              <p className="text-2xl font-bold">{employees.length}</p>
            </div>
            <DollarSign className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Salary Added
              </p>
              <p className="text-2xl font-bold">
                {employees.filter((emp) => emp.salaryStatus === "added").length}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Salary Not Added
              </p>
              <p className="text-2xl font-bold">
                {
                  employees.filter((emp) => emp.salaryStatus === "not_added")
                    .length
                }
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Data Table */}
      <SimpleDataTable columns={salaryColumns} data={employees} />
    </div>
  );
}
